import React from 'react'
import PropTypes from 'prop-types'
import {
  CircularProgress,
  Grid,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@material-ui/core'
import AutocompleteNative from '@material-ui/lab/Autocomplete'
import AssignmentIcon from '@material-ui/icons/Assignment'

import { ModalConfirmacao, SecaoGenerica, CustomTable } from 'components'
import { useResultadoNecropapiloscopico } from './hooks/useResultadoNecropapiloscopico'
import { useStyles } from './ResultadoNecropapiloscopico.styles'

export const ResultadoNecropapiloscopico = (props) => {
  const { carregando, idExameRealizado, idExameSolicitado, setDisableBotaoAvancar } = props
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const {
    openAnalise,
    setOpenAnalise,
    processandoAnalise,
    processandoConfirmacaoAnalise,
    vestigiosExtraidosDeCadaver,
    processandoVestigiosExtraidosDeCadaver,
    formAnaliseNecropapiloscopica,
    clickOpenVestigio,
    handleConfirmarAnalise,
    verificarVestigioAnalisado,
  } = useResultadoNecropapiloscopico({
    idExameRealizado,
    idExameSolicitado,
    setDisableBotaoAvancar,
  })

  const getResultadoLabel = (classificacao) => {
    switch (classificacao) {
      case 'IDENTIFICADO_DOCUMENTO':
        return 'Confronto com documento disponibilizado - Foram encontrados padrões dactiloscópicos que confirmam a identidade do cadáver. (IDENTIFICADO)'
      case 'NAO_IDENTIFICADO_DOCUMENTO':
        return 'Confronto com documento disponibilizado - Não foram encontrados padrões dactiloscópicos que confirmam a identidade do cadáver. (NÃO IDENTIFICADO)'
      case 'IDENTIFICADO_SISTEMA':
        return 'Busca no sistema VSOFT - Foram encontrados padrões dactiloscópicos que confirmam a identidade do cadáver. (IDENTIFICADO)'
      case 'NAO_IDENTIFICADO_SISTEMA':
        return 'Busca no sistema VSOFT - Não foram encontrados padrões dactiloscópicos que confirmam a identidade do cadáver. (NÃO IDENTIFICADO)'
      case 'NAO_IDENTIFICADO_FALTA_DOCUMENTO':
        return 'Não identificado por falta de documento'
      case 'NAO_IDENTIFICADO_SEM_IMPRESSAO_PAPILAR':
        return 'Não identificado por impossibilidade de extração de impressão papilar'
      default:
        return ''
    }
  }

  const labelAnalise = (rowData) => {
    let label = 'Não analisada'
    if (rowData.anaNecPaps) {
      rowData.anaNecPaps.forEach((analise) => {
        if (analise.id_exame_necropapiloscopico === idExameRealizado) {
          label =
            analise.resultado === 'IDENTIFICADO_DOCUMENTO' ||
              analise.resultado === 'IDENTIFICADO_SISTEMA'
              ? 'Resultado: Identificado'
              : 'Resultado: Não identificado'
        }
      })
    }
    return label
  }

  const columns = [
    {
      title: 'IDENTIFICADOR',
      render: (rowData) => <Typography style={{ fontSize: '13px' }}>{rowData.identificador}</Typography>,
    },
    {
      title: 'DESCRIÇÃO',
      render: (rowData) => <Typography style={{ fontSize: '13px' }}>{rowData.descricao?.toUpperCase()}</Typography>,
    },
    {
      title: 'CADÁVER (NIC)',
      render: (rowData) => <Typography style={{ fontSize: '13px' }}>{rowData.cadavOrig?.nic?.toUpperCase()}</Typography>,
    },
    {
      title: 'ANÁLISE',
      render: (rowData) => <Typography style={{ fontSize: '13px' }}>{labelAnalise(rowData)}</Typography>,
    },
    {
      title: '',
      render: (rowData) => (
        <Tooltip title="Informar resultado da análise necropapiloscópica">
          <IconButton onClick={() => clickOpenVestigio(rowData)}>
            <AssignmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <div>
      {carregando || processandoVestigiosExtraidosDeCadaver ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <Grid container>
          <SecaoGenerica titulo="LISTA DE VESTÍGIOS EXTRAÍDOS DE CADÁVER">
            <Grid item md={12} sm={12} xs={12} className={classes.tableContainer}>
              {isMobile ? (
                vestigiosExtraidosDeCadaver && vestigiosExtraidosDeCadaver.length > 0 ? (
                  <Grid container spacing={2}>
                    {vestigiosExtraidosDeCadaver.map((vestigio) => (
                      <Grid item xs={12} key={vestigio.id}>
                        <div className={classes.vestigioCard}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold' }}>
                                IDENTIFICADOR
                              </Typography>
                              <Typography variant="body2">{vestigio.identificador}</Typography>
                            </Grid>
                            <Grid item xs={6} style={{ textAlign: 'right' }}>
                              <Tooltip title="Informar resultado da análise necropapiloscópica">
                                <IconButton onClick={() => clickOpenVestigio(vestigio)} size="small">
                                  <AssignmentIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold' }}>
                                DESCRIÇÃO
                              </Typography>
                              <Typography variant="body2">{vestigio.descricao?.toUpperCase() || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold' }}>
                                CADÁVER (NIC)
                              </Typography>
                              <Typography variant="body2">{vestigio.cadavOrig?.nic?.toUpperCase() || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold' }}>
                                ANÁLISE
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                {labelAnalise(vestigio)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ fontStyle: 'italic', padding: 16, textAlign: 'center' }}
                  >
                    Nenhum vestígio extraído de cadáver encontrado
                  </Typography>
                )
              ) : (
                <CustomTable
                  title=""
                  columns={columns}
                  data={vestigiosExtraidosDeCadaver || []}
                  isLoading={processandoVestigiosExtraidosDeCadaver}
                  emptyDataSourceMessage="Nenhum vestígio extraído de cadáver encontrado"
                  options={{
                    search: false,
                    pageSize: 10,
                    paging: false,
                    toolbar: false,
                  }}
                />
              )}
            </Grid>
          </SecaoGenerica>

          <ModalConfirmacao
            aberto={openAnalise}
            onClose={() => setOpenAnalise(false)}
            onConfirm={handleConfirmarAnalise}
            processando={processandoConfirmacaoAnalise}
            titulo="Resultado da análise"
          >
            {processandoAnalise ? (
              <div className={classes.carregando}>
                <CircularProgress disableShrink />
              </div>
            ) : (
              <Grid item md={12} sm={12} xs={12}>
                <AutocompleteNative
                  name="resultado"
                  noOptionsText="Sem informações"
                  options={[
                    'IDENTIFICADO_DOCUMENTO',
                    'NAO_IDENTIFICADO_DOCUMENTO',
                    'IDENTIFICADO_SISTEMA',
                    'NAO_IDENTIFICADO_SISTEMA',
                    'NAO_IDENTIFICADO_FALTA_DOCUMENTO',
                    'NAO_IDENTIFICADO_SEM_IMPRESSAO_PAPILAR',
                  ]}
                  value={formAnaliseNecropapiloscopica.formState.values.resultado}
                  onChange={(event, newValue) => {
                    formAnaliseNecropapiloscopica.handleChangeNewValueByNameProperty(
                      event,
                      newValue,
                      'resultado'
                    )
                  }}
                  getOptionLabel={(option) => getResultadoLabel(option)}
                  renderOption={(option) => getResultadoLabel(option)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="RESULTADO DA ANÁLISE"
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </ModalConfirmacao>
        </Grid>
      )}
    </div>
  )
}

ResultadoNecropapiloscopico.propTypes = {
  carregando: PropTypes.bool,
  idExameRealizado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  idExameSolicitado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setDisableBotaoAvancar: PropTypes.func.isRequired,
}
